import { useCallback, useLayoutEffect, useRef, useState } from 'react';
/** Follow the mounted transcript until an explicit reading gesture moves upward. */ export function useChatScroll(identity) {
    const [element, setElement] = useState(null);
    const following = useRef(true);
    const [awayFromBottom, setAwayFromBottom] = useState(false);
    const jumpToLatest = useCallback(()=>{
        following.current = true;
        element?.scrollTo({
            top: element.scrollHeight,
            behavior: 'auto'
        });
        setAwayFromBottom(false);
    }, [
        element
    ]);
    useLayoutEffect(()=>{
        if (!element) return;
        following.current = true;
        let touchY = 0;
        const settle = ()=>{
            if (following.current) element.scrollTo({
                top: element.scrollHeight,
                behavior: 'auto'
            });
            const distance = element.scrollHeight - element.scrollTop - element.clientHeight;
            setAwayFromBottom(distance > 80);
        };
        const onScroll = ()=>{
            if (element.scrollHeight - element.scrollTop - element.clientHeight < 2) following.current = true;
            settle();
        };
        const onWheel = (event)=>{
            if (event.deltaY < 0) following.current = false;
        };
        const onTouchStart = (event)=>{
            touchY = event.touches[0]?.clientY ?? 0;
        };
        const onTouchMove = (event)=>{
            const y = event.touches[0]?.clientY ?? touchY;
            if (y > touchY) following.current = false;
            touchY = y;
        };
        const onKeyDown = (event)=>{
            if (event.target.closest('input, textarea, [contenteditable="true"]')) return;
            if ([
                'ArrowUp',
                'PageUp',
                'Home'
            ].includes(event.key) || event.key === ' ' && event.shiftKey) following.current = false;
        };
        const onPointerDown = (event)=>{
            if (event.target === element && event.clientX >= element.getBoundingClientRect().right - 14) following.current = false;
        };
        const observer = new ResizeObserver(settle);
        observer.observe(element);
        if (element.firstElementChild) observer.observe(element.firstElementChild);
        element.addEventListener('scroll', onScroll, {
            passive: true
        });
        element.addEventListener('wheel', onWheel, {
            passive: true
        });
        element.addEventListener('touchstart', onTouchStart, {
            passive: true
        });
        element.addEventListener('touchmove', onTouchMove, {
            passive: true
        });
        element.addEventListener('keydown', onKeyDown);
        element.addEventListener('pointerdown', onPointerDown);
        settle();
        return ()=>{
            observer.disconnect();
            element.removeEventListener('scroll', onScroll);
            element.removeEventListener('wheel', onWheel);
            element.removeEventListener('touchstart', onTouchStart);
            element.removeEventListener('touchmove', onTouchMove);
            element.removeEventListener('keydown', onKeyDown);
            element.removeEventListener('pointerdown', onPointerDown);
        };
    }, [
        element,
        identity
    ]);
    return {
        scrollRef: setElement,
        awayFromBottom,
        jumpToLatest
    };
}
